import { Group } from "@/company/groups/types";
import { DsSelectMenu } from "@/core/components/layout/ds-select-menu";
import { useGroupInput } from "@/core/components/layout/group-input/hooks";
import { Button } from "@/core/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/core/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/core/components/ui/popover";
import { cn } from "@/core/utils/custom-styles";
import { Check, ChevronDown, Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox } from "../../ui/checkbox";

interface GroupInputProps extends React.ComponentPropsWithoutRef<
  typeof Popover
> {
  inputId: string;
  defaultValue: Group | null;
  hasEmptyValue: boolean;
  placeholder?: string;
  asCheckbox?: boolean;
  onChange: (value: Group | null) => void;
  triggerClassName?: string;
  /**
   * `ds`: campo e menu no visual do InputSelect do Workbase DS (DsSelectMenu),
   * usado no Painel e em Colaboradores. `legacy` (padrão) mantém o
   * Popover/Command das outras telas.
   */
  appearance?: "legacy" | "ds";
  /** Rótulo do campo no visual `ds`. */
  label?: string;
}

/** Opção "todos os centros de custo" do menu `ds` (seleção única). */
const ALL_GROUPS = "__all__";

export function GroupInput({
  inputId,
  defaultValue,
  hasEmptyValue,
  placeholder,
  asCheckbox,
  onChange,
  triggerClassName,
  appearance = "legacy",
  label,
  ...props
}: GroupInputProps) {
  const [translate] = useTranslation(["core"]);
  const { data, isLoading, searchParams, setSearchParams } = useGroupInput();
  const [selectedItem, setSelectedItem] = useState<Group | null>(defaultValue);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [filteredGroups, setFilteredGroups] = useState<Group[]>([]);

  // `selectedItem` é inicializado a partir de `defaultValue`, mas o estado só
  // é lido na montagem — sem este efeito, limpar o valor externamente (ex.:
  // botão "Limpar filtros") não refletiria aqui, mantendo a seleção antiga.
  useEffect(() => {
    setSelectedItem(defaultValue);
  }, [defaultValue]);

  const handleSearch = useCallback(
    async (value: string) => {
      setSearchParams({ ...searchParams, search: value, page: 1 });
    },
    [setSearchParams, searchParams],
  );

  const handleScroll = useCallback(
    async (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget;
      const scrollHeight = target.scrollHeight;
      const scrollTop = target.scrollTop;
      const clientHeight = target.clientHeight;
      const diff = Math.abs(scrollHeight - scrollTop - clientHeight);

      if (diff < 5 && !searchParams.search && !isLoading) {
        setScrollPosition(scrollTop);
        setSearchParams({
          ...searchParams,
          page: (searchParams.page || 1) + 1,
        });
      }
    },
    [setSearchParams, searchParams, isLoading],
  );

  useEffect(() => {
    setFilteredGroups((prev) =>
      searchParams.page === 1 ? data : [...prev, ...data],
    );
  }, [data]);

  useEffect(() => {
    if (scrollPosition > 0) {
      const groupSearch = document.getElementById(`${inputId}-command`);
      const list = groupSearch?.querySelector("[cmdk-list]");

      if (list) {
        list.scrollTop = scrollPosition;
      }
    }
  }, [filteredGroups.length, scrollPosition]);

  const handleSelect = useCallback(
    (group: Group | null) => {
      onChange(group);
      setSelectedItem(group);
      setSearchParams({ ...searchParams, page: 1 });
    },
    [onChange, setSearchParams],
  );

  if (appearance === "ds") {
    // Alguns chamadores passam só o id (number) do filtro salvo: resolve o
    // id e o nome para marcar a opção e exibir o centro de custo no campo.
    const raw = selectedItem as Group | number | null;
    const selectedId = typeof raw === "number" ? raw : (raw?.id ?? null);
    const selectedName =
      (typeof raw === "object" ? raw?.name : undefined) ??
      filteredGroups.find((group) => group.id === selectedId)?.name ??
      null;

    return (
      <DsSelectMenu
        id={inputId}
        label={label}
        placeholder={placeholder ?? ""}
        displayValue={selectedId ? selectedName : null}
        options={[
          // Durante a busca a linha "todos" sai, para o vazio aparecer.
          ...(hasEmptyValue && !searchParams.search
            ? [{ value: ALL_GROUPS, label: placeholder ?? "" }]
            : []),
          ...filteredGroups.map((group) => ({
            value: String(group.id),
            label: group.name,
          })),
        ]}
        selected={selectedId ? [String(selectedId)] : [ALL_GROUPS]}
        onSelect={(option) => {
          if (option.value === ALL_GROUPS) {
            handleSelect(null);
            return;
          }
          const group = filteredGroups.find(
            (item) => String(item.id) === option.value,
          );
          if (group) handleSelect(group);
        }}
        search={{
          value: searchParams.search ?? "",
          onChange: handleSearch,
          placeholder: translate("groups-filter-search-placeholder") as string,
        }}
        emptyText={translate("groups-filter-empty-state") as string}
        loading={isLoading}
        onEndReached={() => {
          if (searchParams.search || isLoading) return;
          setSearchParams({
            ...searchParams,
            page: (searchParams.page || 1) + 1,
          });
        }}
      />
    );
  }

  return (
    <Popover {...props} modal={true}>
      <PopoverTrigger asChild>
        <Button
          id={inputId}
          variant="outline"
          className={cn("justify-between w-full bg-white", triggerClassName)}
        >
          <span className="overflow-hidden font-normal text-ellipsis">
            {selectedItem?.name || placeholder}
          </span>
          <ChevronDown className="opacity-50 size-4" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="p-0 w-[320px]">
        <Command id={`${inputId}-command`}>
          <CommandInput
            placeholder={
              translate("groups-filter-search-placeholder") as string
            }
            className="h-9"
            value={searchParams.search}
            onValueChange={handleSearch}
          />
          <CommandList onScrollCapture={handleScroll}>
            <CommandEmpty>
              {translate("groups-filter-empty-state") as string}
            </CommandEmpty>
            <CommandGroup>
              {hasEmptyValue && (
                <CommandItem
                  value=""
                  onSelect={() => {
                    handleSelect(null);
                  }}
                >
                  {asCheckbox && (
                    <Checkbox className="mr-2" checked={!selectedItem} />
                  )}
                  {placeholder}
                  {!asCheckbox && (
                    <Check
                      className={cn(
                        "ml-auto size-4",
                        !selectedItem ? "opacity-100" : "opacity-0",
                      )}
                    />
                  )}
                </CommandItem>
              )}
              {filteredGroups.map((group, index) => (
                <CommandItem
                  key={`${group.id}-${index}`}
                  value={group.name}
                  onSelect={() => {
                    handleSelect(group);
                  }}
                >
                  {asCheckbox && (
                    <Checkbox
                      className="mr-2"
                      checked={selectedItem?.id === group.id || !selectedItem}
                    />
                  )}
                  {group.name}
                  {!asCheckbox && (
                    <Check
                      className={cn(
                        "ml-auto size-4",
                        selectedItem?.id === group.id
                          ? "opacity-100"
                          : "opacity-0",
                      )}
                    />
                  )}
                </CommandItem>
              ))}
              {isLoading && <Loader2 className="mx-auto animate-spin size-4" />}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
